import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import type { SxProps, Theme } from '@mui/material';

export type BreadcrumbItem = {
  /** Displayed label */
  label: string;
  /** Navigate to URL (renders as <a href>) */
  href?: string;
  /** Navigate via callback (renders as <button>) */
  onClick?: () => void;
};

type AppBreadcrumbsProps = {
  items: BreadcrumbItem[];
  /** Extra sx applied to the outer Box wrapper */
  sx?: SxProps<Theme>;
  /** Show bottom border (default: false) */
  withBorder?: boolean;
};

/**
 * Shared breadcrumb component used in grid detail pages and kanban inline detail.
 *
 * - All items except the last are rendered as clickable links or buttons.
 * - Links with onClick intercept unmodified primary clicks for SPA navigation;
 *   modified clicks remain native browser link actions.
 * - The last item is always rendered as plain text (current page).
 */
export const AppBreadcrumbs = ({ items, sx, withBorder = false }: AppBreadcrumbsProps) => {
  if (!items.length) return null;

  const crumbs = items.slice(0, -1);
  const current = items[items.length - 1];

  return (
    <Box
      sx={{
        py: 1,
        px: 2,
        ...(withBorder && { borderBottom: 1, borderColor: 'divider' }),
        ...sx,
      }}
    >
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
        {crumbs.map((item, i) =>
          item.href ? (
            <Link
              key={i}
              underline="hover"
              color="inherit"
              href={item.href}
              onClick={
                item.onClick
                  ? (event) => {
                      if (
                        event.button !== 0 ||
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey
                      ) {
                        return;
                      }
                      event.preventDefault();
                      item.onClick?.();
                    }
                  : undefined
              }
              sx={{ fontSize: '0.875rem' }}
            >
              {item.label}
            </Link>
          ) : item.onClick ? (
            <Link
              key={i}
              component="button"
              underline="hover"
              color="inherit"
              onClick={item.onClick}
              sx={{ cursor: 'pointer', fontSize: '0.875rem' }}
            >
              {item.label}
            </Link>
          ) : (
            <Link
              key={i}
              underline="hover"
              color="inherit"
              href={item.href}
              sx={{ fontSize: '0.875rem' }}
            >
              {item.label}
            </Link>
          )
        )}
        <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>
          {current.label}
        </Typography>
      </Breadcrumbs>
    </Box>
  );
};
